// What travels between the two phones.
//
// The host owns the simulation; the guest owns one direction. So the wire has
// exactly two shapes: a snapshot going one way and a direction going the other.
// Nothing here touches the network — it is all pure, so the format can be
// tested without a peer connection.
//
// The food is the interesting part. There are up to 250 cells and the guest can
// rebuild the STARTING layout itself from the board and the pie count, because
// freshFood() is deterministic. So the only thing that needs sending is which
// ones are gone, as a bitmask over a stable ordering: 250 bits is 32 bytes, and
// — the reason it is a mask rather than a list of what was just eaten — it is
// idempotent. A dropped packet on an unreliable channel costs one frame of
// staleness instead of permanently desyncing the board.

import { DIR_LIST, makeBoard, key } from './maze.js';
import { freshFood } from './engine.js';

/** Directions travel as an index into DIR_LIST; 255 means "not moving". */
export const NO_DIR = 255;
export const dirIndex = (dir) => {
  const i = DIR_LIST.findIndex((d) => d.x === dir?.x && d.y === dir?.y);
  return i === -1 ? NO_DIR : i;
};
export const dirFromIndex = (i) => DIR_LIST[i] ?? null;

/** The cells that can ever hold food, in a fixed order both sides agree on. */
export function foodOrder(board, extraPies) {
  return [...freshFood(board, extraPies).keys()];
}

/** Which of those cells have been eaten, one bit each. */
export function packFood(order, food) {
  const bytes = new Uint8Array(Math.ceil(order.length / 8));
  for (let i = 0; i < order.length; i += 1) {
    if (!food.has(order[i])) bytes[i >> 3] |= 1 << (i & 7);
  }
  return bytes;
}

/** Rebuild a food map from the starting layout plus the eaten mask. */
export function unpackFood(board, extraPies, order, bytes) {
  const fresh = freshFood(board, extraPies);
  for (let i = 0; i < order.length; i += 1) {
    if (bytes[i >> 3] & (1 << (i & 7))) fresh.delete(order[i]);
  }
  return fresh;
}

// Base64 keeps the mask inside a JSON string. `globalThis` rather than a bare
// name so this file also runs under node, where the tests live.
const b64 = (bytes) => {
  let out = '';
  for (const b of bytes) out += String.fromCharCode(b);
  return globalThis.btoa(out);
};
const unb64 = (text) => Uint8Array.from(globalThis.atob(text), (c) => c.charCodeAt(0));

/** Everything the guest needs once, to build the same board. */
export const encodeHello = (game) => JSON.stringify({
  t: 'hello',
  board: game.board.id,
  diff: game.diff.id,
  speed: game.speed.id,
  ghosts: game.ghostCount,
  pies: game.extraPies,
});

/** Two decimals is a fifth of a pixel at any tile size anyone plays at. */
const r2 = (n) => Math.round(n * 100) / 100;

export function encodeTick(game, order) {
  return JSON.stringify({
    t: 'tick',
    s: game.state,
    sc: game.score,
    lv: game.lives,
    l: game.level,
    m: game.mode,
    f: r2(game.frightLeft),
    mo: r2(game.mouth),
    fl: r2(game.flash),
    tm: r2(game.timer),
    n: [r2(game.nick.x), r2(game.nick.y), dirIndex(game.nick.dir)],
    r: game.rafs.map((g) => [
      r2(g.x), r2(g.y), dirIndex(g.dir), g.eaten ? 1 : 0, g.player ? 1 : 0,
    ]),
    e: b64(packFood(order, game.food)),
  });
}

/**
 * Rebuild something the renderer can draw.
 *
 * Deliberately shaped like a game rather than converted into one: the renderer
 * takes a game and reads a dozen fields off it, so a snapshot that carries the
 * same field names can be drawn by the same code with no branch for "remote".
 */
export function decodeTick(msg, board, extraPies, order) {
  return {
    board,
    state: msg.s,
    score: msg.sc,
    lives: msg.lv,
    level: msg.l,
    mode: msg.m,
    frightLeft: msg.f,
    mouth: msg.mo,
    flash: msg.fl,
    timer: msg.tm,
    nick: { x: msg.n[0], y: msg.n[1], dir: dirFromIndex(msg.n[2]) },
    rafs: msg.r.map((g) => ({
      x: g[0], y: g[1], dir: dirFromIndex(g[2]), eaten: !!g[3], player: !!g[4],
    })),
    food: unpackFood(board, extraPies, order, unb64(msg.e)),
  };
}

export const encodeInput = (dir) => JSON.stringify({ t: 'in', d: dirIndex(dir) });

/** Everything a guest needs to stand up its own board from a hello. */
export function boardFromHello(msg) {
  return { board: makeBoard(msg.board), extraPies: msg.pies };
}

export { key };
